// Mock data for the Horses Review screen (approved HTML:
// 08-Horses-Review-v2-Tighter-Spacing.html). Represents Doug's world:
// Sarah Mitchell with Duke (Quarter Horse, Reining) on Gut X; the design
// also surfaces missing fields (birth date, daily amount, customer goal)
// per Doug's standing rule to keep "not discussed", "unknown" and
// "declined" distinct.
//
// Standing memory (per project_100x_equine_seo_consolidation): never use
// the banned condition-claim phrase. This file surfaces "Gut X" without
// any condition claim; the HTML source does the same.
//
// TODO(runtime): swap mock -> real API once services land:
//   - services/horse-timeline summarize call (confirmed service, no HTTP
//     read endpoint yet)
//   - services/customer-coverage for colic summary
//   - dist/account-workflows.saveAccountFormDraft for edit drafts
//     (confirmed in dist/account-workflows.mjs line 64)

import { HORSE_PORTRAIT_PLACEHOLDER } from '../../data/mockData.js';

export type HorseStatus = 'Ownership not confirmed' | 'Currently owned' | 'No longer owned' | 'Sold' | 'Deceased';
export type FieldStatus = 'Not discussed' | 'Provided' | 'Customer does not know' | 'Customer declined';

export interface HorseProfile {
  id: string;
  name: string;
  breed: string;
  discipline: string;
  reportedAge: string;
  birthDate: string;
  horseStatus: HorseStatus;
  portraitUrl: string;
  storyText: string;
  storyAuthorNote: string;
  reportedProduct: {
    name: string;
    square: string;
    reportedUseLabel: string;
    daysOfSupply: string;
    dailyAmount: string;
    feedingRoutine: string;
  };
  goals: {
    reason: string;
    goal: string;
  };
  completeness: Array<{ key: string; label: string }>;
  colicCoverage: {
    status: 'Not recorded' | 'Needs information' | 'Confirmed' | 'Pending acceptance';
    detailLine: string;
  };
  relationships: {
    barn: string;
    trainer: string;
    veterinarian: string;
  };
  profileHistory: Array<{
    title: string;
    detail: string;
    note: string;
  }>;
  nextQuestion: {
    prompt: string;
    helper: string;
    editor: 'routine' | 'goals' | 'profile' | 'story';
  };
}

export const DUKE_PROFILE: HorseProfile = {
  id: 'horse_duke',
  name: 'Duke',
  breed: 'Quarter Horse',
  discipline: 'Reining',
  reportedAge: '9 years',
  birthDate: 'Not recorded',
  horseStatus: 'Currently owned',
  portraitUrl: HORSE_PORTRAIT_PLACEHOLDER,
  storyText: '',
  storyAuthorNote: "Add Sarah's story in her own words.",
  reportedProduct: {
    name: 'Gut X',
    square: 'GX',
    reportedUseLabel: 'Reported use for Duke · Example',
    daysOfSupply: 'Daily amount needed',
    dailyAmount: 'Not discussed',
    feedingRoutine: 'Not discussed',
  },
  goals: {
    reason: 'Not discussed yet',
    goal: 'Not discussed yet',
  },
  completeness: [
    { key: 'birth', label: 'Birth date' },
    { key: 'dose', label: 'Daily product amount' },
    { key: 'goal', label: "Customer's goal" },
  ],
  colicCoverage: {
    status: 'Needs information',
    detailLine: 'Birth date and enrollment checks are still needed. Coverage is not confirmed.',
  },
  relationships: {
    barn: 'Not recorded',
    trainer: 'Not recorded',
    veterinarian: 'Not recorded',
  },
  profileHistory: [
    {
      title: 'Profile ready for review',
      detail: "Confirm missing details during Sarah's next conversation.",
      note: 'Illustrative profile · No live updates',
    },
  ],
  nextQuestion: {
    prompt: '"How are you using Gut X with Duke each day?"',
    helper: 'Confirm the amount, feeding routine and what Sarah is hoping to see.',
    editor: 'routine',
  },
};

export const HORSE_PROFILES: HorseProfile[] = [DUKE_PROFILE];

export type EditorMode = 'profile' | 'routine' | 'goals' | 'story' | 'add';

export const EDITOR_TITLES: Record<EditorMode, string> = {
  profile: "Edit Duke's profile",
  routine: "Duke's daily routine",
  goals: "Sarah's goals for Duke",
  story: "Duke's story",
  add: 'Add a horse',
};

export const FIELD_STATUS_OPTIONS: FieldStatus[] = [
  'Not discussed',
  'Provided',
  'Customer does not know',
  'Customer declined',
];

export const HORSE_STATUS_OPTIONS: HorseStatus[] = [
  'Ownership not confirmed',
  'Currently owned',
  'No longer owned',
  'Sold',
  'Deceased',
];
